import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  CatalogAdminClient,
  CatalogSupplierClient,
  CatalogVersionDto,
  CurrentCatalogVersionDto,
  SupplierCatalogReferentialDto,
  TjmEntryTargetDto,
} from '@/api/catalog-client';
import {
  useCatalogAdminClient,
  useCatalogSupplierClient,
} from '@/api/catalog-client-context';

// =============================================================================
// use-catalog.ts — Hooks TanStack Query de LECTURE du BC catalog (S8-D2).
// =============================================================================
// Lecture SEULE (browser versions A-08, version courante F-05) en useQuery. AUCUNE
// mutation : pas d'édition, pas d'upload, pas de figement (S9/S11, hors D2). Les
// clients effectifs viennent des providers catalog (doublables, stub-first).
//
// ZÉRO règle métier ici : l'autorité (RBAC admin vs supplier, transitions d'état) est
// l'API. Les hooks relaient l'intention de lecture ; l'erreur API remonte telle quelle.
// P-05 : deux hooks distincts, sur deux clients distincts (admin vs fournisseur).

const CATALOG_KEY = ['catalog'] as const;

/** Clé de query du browser des versions (A-08, admin). */
export const catalogVersionsKey = [...CATALOG_KEY, 'admin', 'versions'] as const;

/** Clé de query de la version publiée courante (F-05, fournisseur). */
export const catalogCurrentKey = [...CATALOG_KEY, 'supplier', 'current'] as const;

/** Clé de query du référentiel de la PUBLISHED courante (Uc-CAT-PROFILS, fournisseur). */
export const catalogReferentialKey = [
  ...CATALOG_KEY,
  'supplier',
  'referential',
] as const;

/** Racine de clé de la cible de saisie TJM par profil (R15-CMP-03, fournisseur). */
export const catalogTjmEntryKey = [
  ...CATALOG_KEY,
  'supplier',
  'tjm-entry-target',
] as const;

/** GET browser des versions du catalogue (A-08, gardé admin). */
export function useCatalogVersions(): UseQueryResult<
  readonly CatalogVersionDto[],
  Error
> {
  const client: CatalogAdminClient = useCatalogAdminClient();
  return useQuery<readonly CatalogVersionDto[], Error>({
    queryKey: catalogVersionsKey,
    queryFn: () => client.listVersions(),
  });
}

/** GET version publiée courante (F-05, gardé supplier). `null` = aucune version publiée. */
export function useCurrentCatalogVersion(): UseQueryResult<
  CurrentCatalogVersionDto | null,
  Error
> {
  const client: CatalogSupplierClient = useCatalogSupplierClient();
  return useQuery<CurrentCatalogVersionDto | null, Error>({
    queryKey: catalogCurrentKey,
    queryFn: () => client.getCurrentVersion(),
  });
}

/**
 * GET référentiel de la PUBLISHED courante (Uc-CAT-PROFILS, gardé supplier) : profils + corrélations.
 * `version === null` = aucune version publiée (listes vides). Lecture seule ; PUBLISHED-only (autorité API).
 */
export function useCurrentCatalogReferential(): UseQueryResult<
  SupplierCatalogReferentialDto,
  Error
> {
  const client: CatalogSupplierClient = useCatalogSupplierClient();
  return useQuery<SupplierCatalogReferentialDto, Error>({
    queryKey: catalogReferentialKey,
    queryFn: () => client.listCurrentReferential(),
  });
}

/**
 * R15-CMP-03 — cible de saisie TJM du fournisseur EN SESSION pour `profileCode` (F0-CONTRACT-4). `null` =
 * aucune campagne ouverte où le fournisseur est invité couvrant ce profil ⇒ PAS de CTA. La query est
 * DÉSACTIVÉE tant qu'aucun profil n'est sélectionné (`profileCode === undefined`) — aucun appel superflu.
 * L'autorité (RLS n°1, grille figée, bucket ouvert) est l'API ; l'UI relaie la lecture.
 */
export function useTjmEntryTarget(
  profileCode: string | undefined,
): UseQueryResult<TjmEntryTargetDto | null, Error> {
  const client: CatalogSupplierClient = useCatalogSupplierClient();
  return useQuery<TjmEntryTargetDto | null, Error>({
    queryKey: [...catalogTjmEntryKey, profileCode ?? null],
    queryFn: () => client.resolveTjmEntryTarget(profileCode as string),
    enabled: profileCode !== undefined,
  });
}
